import {Trans} from '@lingui/macro';
import React from 'react';
import styled from 'styled-components';

import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {RuleActionDefinition, RuleConditionDefinition} from '../ruleParameterModel';

/*
 * Types.
 */

interface RuleActionConditionTooltipProps {
  conditionOrActionDefinition: RuleActionDefinition | RuleConditionDefinition;
}

/*
 * Styles.
 */

const StyledLink = styled.a`
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.white};
  text-decoration: underline;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  margin-right: 5px;
`;

/*
 * Component.
 */

const RuleActionConditionTooltip = ({conditionOrActionDefinition}: RuleActionConditionTooltipProps) => {
  const theme = useUserTheme();
  const {help, tooltip} = conditionOrActionDefinition;

  if (!help && !tooltip) {
    return null;
  }

  const render = () => {
    if (help) {
      return (
        <div>
          {`${help.text} `}
          <StyledLink target="_blank" rel="noopener noreferrer" href={help.link}>
            <Trans id="rule-action-condition-tooltip-learn-more">Learn more</Trans>
          </StyledLink>
        </div>
      );
    }

    return tooltip;
  };

  return (
    <StyledTooltipCoordinator
      iconColor={theme.greys.shade70}
      maxWidth={300}
      persistent={Boolean(help)}
      placement={help ? 'auto' : undefined}
      render={render}
    />
  );
};

/*
 * Helpers.
 */

export function maybeRuleActionConditionTooltip(
  conditionOrActionDefinition: RuleActionDefinition | RuleConditionDefinition,
) {
  const {help, tooltip} = conditionOrActionDefinition;

  return help || tooltip ? (
    <RuleActionConditionTooltip conditionOrActionDefinition={conditionOrActionDefinition} />
  ) : undefined;
}
